import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface LeadgenForm {
  form_id: string;
  form_name: string;
  status: string;
  page_id: string;
  leads_count: number;
  created_time: string | null;
  display_name: string;
}

export function useCachedLeadgenForms(userId: string | undefined, pageId: string | undefined) {
  return useQuery({
    queryKey: ["cached-leadgen-forms", userId, pageId],
    queryFn: async (): Promise<LeadgenForm[]> => {
      // Step 1: Check Supabase cache first
      const { data: cachedData, error: cacheError } = await supabase
        .from("meta_cached_leadgen_forms")
        .select("form_id, form_name, status, page_id, leads_count, created_time, display_name")
        .eq("user_id", userId!)
        .eq("page_id", pageId!)
        .order("form_name");

      if (cacheError) {
        console.error("[useCachedLeadgenForms] Cache query error:", cacheError);
        throw cacheError;
      }

      // Step 2: If cache has data, return it
      if (cachedData && cachedData.length > 0) {
        console.log(`[useCachedLeadgenForms] Cache hit: ${cachedData.length} forms`);
        return cachedData.map((row) => ({
          form_id: row.form_id,
          form_name: row.form_name || "",
          status: row.status || "",
          page_id: row.page_id,
          leads_count: row.leads_count || 0,
          created_time: row.created_time,
          display_name: row.display_name || `${row.form_name} (${row.form_id})`,
        }));
      }

      // Step 3: Cache miss - fetch via n8n-proxy
      console.log("[useCachedLeadgenForms] Cache miss, fetching via n8n-proxy...");

      const { data: proxyData, error: proxyError } = await supabase.functions.invoke("n8n-proxy", {
        body: {
          action: "GET_LEADGEN_FORMS",
          payload: { page_id: pageId },
        },
      });

      if (proxyError) {
        throw new Error(`Failed to fetch leadgen forms: ${proxyError.message}`);
      }

      // n8n-proxy returns the n8n response directly
      const result = proxyData;

      if (!result?.data || result.data.length === 0) {
        console.log("[useCachedLeadgenForms] No forms returned from webhook");
        return [];
      }

      console.log(`[useCachedLeadgenForms] Fetched ${result.data.length} forms from Meta`);
      return result.data;
    },
    enabled: !!userId && !!pageId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
